<template>
  <div class="adv-wrap" v-if="context && context.applicable">
    <!-- Launcher: hidden until pressed (full mode only; the tabbed
         Circumstances/Recommendation views are always open) -->
    <button v-if="!isOpen" type="button" class="adv-launch" @click="launch">
      <span class="adv-spark">✦</span> Ask the SSAG advisor
      <span class="adv-launch-sub">where in the range does this land?</span>
    </button>

    <section v-else class="adv">
      <div class="adv-head">
        <span class="adv-title">🧭 {{ mode === 'result' ? 'SSAG recommendation' : 'SSAG range advisor' }}</span>
        <div class="adv-headright">
          <div class="adv-tone">
            <button type="button" :class="['adv-tonebtn', { on: tone === 'plain' }]" @click="tone = 'plain'">Plain</button>
            <button type="button" :class="['adv-tonebtn', { on: tone === 'professional' }]" @click="tone = 'professional'">Pro</button>
          </div>
          <button v-if="mode === 'full'" type="button" class="adv-close" @click="close" title="Close">✕</button>
        </div>
      </div>

      <div class="adv-body">
        <!-- Unanswered-questions hint (result mode only) -->
        <p v-if="mode === 'result' && unansweredCount > 0" class="adv-lead">
          {{ unansweredCount }} of {{ totalSteps }} questions are still unanswered. The suggestion
          below uses what is known so far; answer the rest on the Circumstances tab to sharpen it.
        </p>

        <!-- Progress -->
        <div v-if="!atResult" class="adv-progress">
          <div class="adv-progbar"><div class="adv-progfill" :style="{ width: progressPct + '%' }"></div></div>
          <span class="adv-progtext">Question {{ Math.min(stepIndex + 1, totalSteps) }} of {{ totalSteps }}</span>
        </div>

        <!-- One question at a time -->
        <div v-if="!atResult && currentStep" class="adv-step">
          <p v-if="stepIndex === 0" class="adv-lead">
            A few plain questions to place this case in the range, no legal jargon. I’ll work out the
            income gap and a few other things from the numbers you’ve entered.
          </p>
          <div class="adv-section" v-if="currentStep.section">{{ currentStep.section }}</div>
          <div class="adv-qtext">{{ personalize(currentStep.question) }}</div>
          <div class="adv-help" v-if="currentStep.help">{{ personalize(currentStep.help) }}</div>
          <div class="adv-choices" :style="{ gridTemplateColumns: 'repeat(' + currentStep.options.length + ', minmax(0, 1fr))' }">
            <button
              v-for="o in currentStep.options"
              :key="String(o.value)"
              type="button"
              :class="['adv-choice', { on: currentValue === o.value }]"
              @click="choose(o.value)">
              {{ o.label }}
            </button>
          </div>
          <button v-if="stepIndex > 0" type="button" class="adv-back" @click="back">← Back</button>
        </div>

        <!-- Questions complete (questions mode hands off to the Recommendation tab) -->
        <div v-if="atResult && mode === 'questions'" class="adv-result-wrap">
          <div class="adv-result">
            <strong>All questions answered</strong>
            <p class="adv-entnote">Open the Recommendation tab to see the suggested position, with the budget comparison and duration guidance.</p>
          </div>
          <div class="adv-result-actions">
            <button type="button" class="adv-back" @click="back">← Revise answers</button>
            <button type="button" class="adv-reset" @click="restart">Start over</button>
          </div>
        </div>

        <!-- Result -->
        <div v-if="atResult && mode !== 'questions'" class="adv-result-wrap">
          <!-- Entitlement summary (derived from the answers) -->
          <div :class="['adv-result', { 'adv-warn': !advice.hasEntitlement }]">
            <strong>{{ advice.hasEntitlement ? 'Entitlement' : 'Entitlement first' }}</strong>
            <p class="adv-entnote">{{ toneText(advice.entitlementNote) }}</p>

            <template v-if="advice.hasEntitlement">
              <div class="adv-position">
                <span class="adv-poslabel">{{ advice.positionLabel }}</span>
                <span class="adv-posdollar">≈ ${{ fmtMoney(advice.dollar) }}/mo</span>
              </div>
              <div class="adv-scalewrap" v-if="context.high > context.low">
                <div class="adv-scale"><div class="adv-marker" :style="{ left: markerPct + '%' }"></div></div>
                <div class="adv-scaleends">
                  <span>Low ${{ fmtMoney(context.low) }}</span>
                  <span>High ${{ fmtMoney(context.high) }}</span>
                </div>
              </div>

              <p class="adv-note" v-if="advice.withChildUpperHalfNote">{{ toneText(advice.withChildUpperHalfNote) }}</p>

              <div class="adv-reasons">
                <div class="adv-rcol" v-if="advice.reasonsHigh.length">
                  <div class="adv-rhead adv-rhigh">Points higher</div>
                  <ul><li v-for="(r, i) in advice.reasonsHigh" :key="'h' + i">{{ toneText(r) }}</li></ul>
                </div>
                <div class="adv-rcol" v-if="advice.reasonsLow.length">
                  <div class="adv-rhead adv-rlow">Points lower</div>
                  <ul><li v-for="(r, i) in advice.reasonsLow" :key="'l' + i">{{ toneText(r) }}</li></ul>
                </div>
              </div>

              <div class="adv-auto" v-if="advice.autoNotes.length">
                <div class="adv-autohead">From the numbers you entered</div>
                <ul><li v-for="(a, i) in advice.autoNotes" :key="'a' + i">{{ toneText(a) }}</li></ul>
              </div>

              <p class="adv-note" v-if="advice.parentingNote">{{ toneText(advice.parentingNote) }}</p>

              <!-- Duration -->
              <div class="adv-duration" v-if="advice.duration">
                <div class="adv-durhead">How long</div>
                <div class="adv-position" v-if="advice.duration.indefinite">
                  <span class="adv-poslabel">Indefinite</span>
                  <span class="adv-posdollar">no fixed end, reviewable</span>
                </div>
                <div class="adv-position" v-else-if="advice.duration.suggestedYears != null">
                  <span class="adv-poslabel">{{ advice.duration.positionLabel || 'Suggested' }}</span>
                  <span class="adv-posdollar">≈ {{ fmtYears(advice.duration.suggestedYears) }}<span v-if="advice.duration.high > advice.duration.low"> (of {{ advice.duration.low.toFixed(1) }}–{{ advice.duration.high.toFixed(1) }} yrs)</span></span>
                </div>
                <ul class="adv-durreasons" v-if="advice.duration.rationale.length">
                  <li v-for="(d, i) in advice.duration.rationale" :key="'d' + i">{{ toneText(d) }}</li>
                </ul>
                <p class="adv-note" v-if="advice.duration.testNote">{{ toneText(advice.duration.testNote) }}</p>
                <p class="adv-note adv-retire" v-if="advice.duration.retirementNote">{{ toneText(advice.duration.retirementNote) }}</p>
                <p class="adv-note" v-if="advice.durationNote">{{ toneText(advice.durationNote) }}</p>
              </div>
            </template>

            <ul class="adv-boundary" v-if="advice.boundaryNotes.length">
              <li v-for="(b, i) in advice.boundaryNotes" :key="'b' + i">⚠ {{ toneText(b) }}</li>
            </ul>
          </div>

          <div class="adv-result-actions" v-if="mode !== 'result'">
            <button type="button" class="adv-back" @click="back">← Revise answers</button>
            <button type="button" class="adv-reset" @click="restart">Start over</button>
          </div>
        </div>

        <p class="adv-disclaimer">{{ advice.disclaimer }}</p>
      </div>
    </section>
  </div>
</template>

<script>
import { askQuestions, computeAdvice, makeEmptyAnswers } from '../ssagRangeAdvisor.js'

export default {
  name: 'SsagRangeAdvisor',
  props: {
    // Read-only snapshot from the parent's computeds (incl. incomes, recipient
    // age/disability so the engine can infer the income gap etc.).
    context: { type: Object, default: () => ({}) },
    // Optional SHARED answers object owned by the parent (makeEmptyAnswers()
    // shape). When provided, this component reads and writes the parent's
    // object directly (the keys pre-exist, so Vue 2 reactivity tracks the
    // writes) so several mounts — wizard panel, Circumstances tab,
    // Recommendation tab — stay in sync. When omitted, answers are local.
    answers: { type: Object, default: null },
    // 'full' = launcher + questions + result (the original wizard panel).
    // 'questions' = always open, questions only (Circumstances tab).
    // 'result' = always open, result only (Recommendation tab).
    mode: { type: String, default: 'full' },
  },
  data() {
    return {
      started: false,
      stepIndex: this.mode === 'result' ? Number.MAX_SAFE_INTEGER : 0,
      tone: 'plain',
      localAnswers: makeEmptyAnswers(),
    }
  },
  created() {
    // With shared answers (tabbed workspace), resume at the first unanswered
    // question instead of replaying answered ones from the top.
    if (this.mode !== 'result' && this.answers) {
      const steps = askQuestions(this.context, this.answers)
      const idx = steps.findIndex(q => this.answers[q.id] == null)
      this.stepIndex = idx === -1 ? steps.length : idx
    }
  },
  computed: {
    answersModel() { return this.answers || this.localAnswers },
    isOpen() { return this.mode !== 'full' || this.started },
    // Answer-aware: the health screening answer can add follow-up severity
    // questions to the flow, so the list re-filters as answers land.
    steps() { return askQuestions(this.context, this.answersModel) },
    totalSteps() { return this.steps.length },
    currentStep() { return this.steps[this.stepIndex] || null },
    currentValue() {
      const st = this.currentStep
      return st ? this.answersModel[st.id] : null
    },
    atResult() { return this.mode === 'result' || this.stepIndex >= this.steps.length },
    unansweredCount() {
      return this.steps.filter(q => this.answersModel[q.id] == null).length
    },
    progressPct() {
      if (!this.totalSteps) return 0
      return Math.round((this.stepIndex / this.totalSteps) * 100)
    },
    advice() { return computeAdvice(this.context, this.answersModel) },
    markerPct() {
      const p = this.advice && this.advice.position != null ? this.advice.position : 0.5
      return Math.min(97, Math.max(3, p * 100))
    },
  },
  methods: {
    launch() { this.started = true; this.stepIndex = 0 },
    close() { this.started = false },
    choose(value) {
      const st = this.currentStep
      if (!st) return
      this.answersModel[st.id] = value
      this.$nextTick(() => { this.stepIndex = Math.min(this.stepIndex + 1, this.steps.length) })
    },
    back() { this.stepIndex = Math.max(0, this.stepIndex - 1) },
    restart() {
      const m = this.answersModel
      Object.keys(m).forEach(k => { m[k] = null })
      this.stepIndex = 0
    },
    fmtMoney(n) { return (Math.round(Number(n) || 0)).toLocaleString('en-CA') },
    fmtYears(n) {
      const v = Number(n) || 0
      const s = (Math.round(v * 2) / 2).toFixed(1).replace(/\.0$/, '')
      return s + ' year' + (v === 1 ? '' : 's')
    },
    // Swap "the recipient" / "the payor" (and any stray "the other party/
    // person/spouse") for the real names, so we never say "the other party".
    personalize(s) {
      if (!s) return ''
      let out = s
      if (this.context.recipientName) out = out.replace(/\bthe recipient\b/gi, this.context.recipientName)
      if (this.context.payorName) {
        out = out.replace(/\bthe payor\b/gi, this.context.payorName)
        out = out.replace(/\bthe other (?:party|person|spouse)\b/gi, this.context.payorName)
      }
      return out
    },
    toneText(s) {
      if (!s) return ''
      const withNames = this.personalize(s)
      if (this.tone === 'professional') return withNames
      return withNames.replace(/\s*\(SSAG[^)]*\)/g, '').replace(/\s{2,}/g, ' ').trim()
    },
  },
}
</script>

<style scoped>
.adv-wrap { margin: 0.75rem 0 0; }

.adv-launch { display: inline-flex; align-items: baseline; gap: 0.4rem; background: #3F6772; color: #fff; border: none; border-radius: 8px; font: inherit; font-weight: 600; padding: 0.8rem 1.4rem; cursor: pointer; }
.adv-launch:hover { background: #365a63; }
.adv-spark { font-size: 0.9em; }
.adv-launch-sub { font-weight: 400; opacity: 0.85; font-size: 0.85em; }

.adv { border: 1px solid var(--border, #d9e0e3); border-radius: 8px; background: var(--surface, #fbfdfe); overflow: hidden; }
.adv-head { display: flex; align-items: center; justify-content: space-between; padding: var(--pad-head, 0.45rem 0.75rem); background: rgba(63,103,114,0.06); }
.adv-title { font-weight: 700; color: var(--text, #24343a); }
.adv-headright { display: flex; align-items: center; gap: 0.5rem; }
.adv-tone { display: inline-flex; border: 1px solid var(--border, #d9e0e3); border-radius: 6px; overflow: hidden; }
.adv-tonebtn { background: #fff; border: none; font: inherit; font-size: var(--fs-tiny, 0.72rem); padding: 0.18rem 0.5rem; cursor: pointer; color: var(--text-subtle, #6b7c83); }
.adv-tonebtn.on { background: #3F6772; color: #fff; }
.adv-close { background: none; border: none; font: inherit; font-size: var(--fs-lead, 0.95rem); color: var(--text-subtle, #6b7c83); cursor: pointer; padding: 0 0.2rem; }
.adv-body { padding: var(--pad-box, 0.6rem 0.8rem); }

.adv-progress { display: flex; align-items: center; gap: 0.6rem; margin-bottom: 0.7rem; }
.adv-progbar { flex: 1; height: 5px; border-radius: 4px; background: #e6edef; overflow: hidden; }
.adv-progfill { height: 100%; background: #3F6772; transition: width 150ms ease; }
.adv-progtext { font-size: var(--fs-tiny, 0.72rem); color: var(--text-subtle, #6b7c83); white-space: nowrap; }

.adv-lead { font-size: var(--fs-small, 0.8rem); color: var(--text-subtle, #6b7c83); margin: 0 0 0.7rem; }
.adv-section { font-size: var(--fs-tiny, 0.72rem); font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: #3F6772; margin-bottom: 0.25rem; }
.adv-qtext { font-size: var(--fs-lead, 0.95rem); font-weight: 600; margin-bottom: 0.3rem; }
.adv-help { font-size: var(--fs-small, 0.8rem); color: var(--text-subtle, #6b7c83); margin-bottom: 0.6rem; }
.adv-choices { display: grid; gap: 0.4rem; }
.adv-choice { text-align: center; background: #fff; border: 1px solid var(--border, #d9e0e3); border-radius: 8px; font: inherit; font-size: var(--fs-body, 0.875rem); line-height: 1.2; padding: 0.5rem 0.4rem; cursor: pointer; }
.adv-choice:hover { border-color: #3F6772; background: rgba(63,103,114,0.04); }
.adv-choice.on { border-color: #3F6772; background: rgba(63,103,114,0.10); font-weight: 600; }
.adv-back { margin-top: 0.7rem; background: none; border: none; font: inherit; font-size: var(--fs-small, 0.8rem); color: #3F6772; cursor: pointer; padding: 0; }

.adv-result { padding: var(--pad-box, 0.6rem 0.8rem); border-radius: 8px; background: rgba(63,103,114,0.05); border: 1px solid var(--border, #d9e0e3); }
.adv-warn { background: #fff7ed; border-color: #f0d8b8; }
.adv-entnote { margin: 0.3rem 0 0.6rem; font-size: var(--fs-small, 0.8rem); }
.adv-position { display: flex; align-items: baseline; gap: 0.6rem; }
.adv-poslabel { font-size: var(--fs-title, 1.05rem); font-weight: 700; color: #3F6772; }
.adv-posdollar { font-size: var(--fs-title, 1.05rem); font-weight: 600; }
.adv-scalewrap { margin: 0.55rem 0 0.4rem; }
.adv-scale { position: relative; height: 8px; border-radius: 5px; background: linear-gradient(90deg, #cfe0e5, #3F6772); }
.adv-marker { position: absolute; top: -3px; width: 3px; height: 14px; background: #24343a; border-radius: 2px; transform: translateX(-50%); }
.adv-scaleends { display: flex; justify-content: space-between; font-size: var(--fs-tiny, 0.72rem); color: var(--text-subtle, #6b7c83); margin-top: 0.2rem; }
.adv-note { margin: 0.4rem 0; font-size: var(--fs-small, 0.8rem); color: var(--text-subtle, #6b7c83); }
.adv-reasons { display: flex; flex-wrap: wrap; gap: 1rem; margin: 0.5rem 0; }
.adv-rcol { flex: 1 1 45%; min-width: 170px; }
.adv-rhead { font-size: var(--fs-small, 0.8rem); font-weight: 700; margin-bottom: 0.2rem; }
.adv-rhigh { color: #2f7d4f; }
.adv-rlow { color: #b4633a; }
.adv-reasons ul, .adv-auto ul { margin: 0; padding-left: 1.1rem; }
.adv-reasons li, .adv-auto li { font-size: var(--fs-small, 0.8rem); margin: 0.1rem 0; }
.adv-auto { margin: 0.5rem 0; padding: 0.4rem 0.6rem; background: rgba(63,103,114,0.05); border-radius: 6px; }
.adv-autohead { font-size: var(--fs-tiny, 0.72rem); font-weight: 700; color: var(--text-subtle, #6b7c83); text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 0.15rem; }
.adv-duration { margin: 0.6rem 0 0; padding: 0.5rem 0.6rem; background: rgba(63,103,114,0.05); border-radius: 6px; }
.adv-durhead { font-size: var(--fs-tiny, 0.72rem); font-weight: 700; color: var(--text-subtle, #6b7c83); text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 0.25rem; }
.adv-durreasons { margin: 0.3rem 0 0; padding-left: 1.1rem; }
.adv-durreasons li { font-size: var(--fs-small, 0.8rem); margin: 0.1rem 0; }
.adv-boundary { margin: 0.5rem 0 0; padding-left: 1.1rem; }
.adv-boundary li { font-size: var(--fs-small, 0.8rem); color: #8a5a1c; margin: 0.15rem 0; }
.adv-result-actions { display: flex; gap: 1rem; margin-top: 0.6rem; }
.adv-reset { background: none; border: none; font: inherit; font-size: var(--fs-small, 0.8rem); color: var(--text-subtle, #6b7c83); text-decoration: underline; cursor: pointer; padding: 0; }
.adv-disclaimer { margin: 0.75rem 0 0; font-size: var(--fs-tiny, 0.72rem); font-style: italic; color: var(--text-subtle, #6b7c83); }
</style>
